import { forwardRef, type HTMLProps, type LegacyRef } from 'react';
import assert from 'assert';
import type { ApiUser } from '@highimpact-frontend/api-types';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { isDataState } from '../../store/data/state';
import StateDecorator from '../utils/state/StateDecorator';
import { createValidator, useForm } from '../../utils/hooks/useForm';
import { useSubmit } from '../../utils/hooks/useSubmit';
import CustomizedDialog from '../utils/MUISpecifics/dialog/CustumizedDialog';
import { Button, Stack } from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import TextInput from '../utils/MUISpecifics/inputText/TextInput';
import DropDownItemSelector from '../utils/MUISpecifics/dropDownItemSelector/DropDownItemSelector';
import { getOrElse } from '../../utils/functions';
import UserService from '../../api/services/config/user/UserService';
import { resetExternalState } from '../../store/data/slice';
import InputMultiType from '../utils/MUISpecifics/inputMultiType/InputMultiType';
import { useTranslation } from 'react-i18next';


// Component props.
export interface FormProps extends HTMLProps<HTMLInputElement> {
  title?: string;
  onCancel: () => void;
  open: boolean;  
}

const defaultValues : ApiUser = {
  role_id: null,
  company_id: null,
  name: '',
  surname: '',
  email: '',
  tel: '',  
  password: '',  
  notification_email: true,
  notification_sms: true,  
};


function CreateUserForm(
  { 
    open: isModalOpen,    
    title, 
    children, 
    ...props 
  }: FormProps,
  ref: LegacyRef<HTMLDivElement> | undefined
) {
  // use translation here : 
  const { t } = useTranslation('components.users'); 

  const dispatch = useAppDispatch();
  const users = useAppSelector((state) => state.data_slice.externals.users);
  const companies = useAppSelector((state) => state.data_slice.externals.companies);
  const roles = useAppSelector((state) => state.data_slice.externals.roles);

  const { submit, state } = useSubmit();
  
  // Handle form submission.  
  const onSubmit = async (data:ApiUser) => {
    submit({
      callbacks: [
        async() => await UserService.add(data),
        () => methods.reset(),
        () => props.onCancel(),
        () => dispatch(resetExternalState({ external_key: 'users' })),
        () => dispatch(resetExternalState({ external_key: 'companies'})),      
      ],
      errorMessages: {
        anyError: t("Une erreur est survenue lors de la création l'utilisateur"),
        unicityError: t('Un utilisateur avec le même email existe déjà'),
      },
      successMessage: t("L'utilisateur a été créé avec succès"),
    })
  }


  /**This is the simplest regular expression, it checks:
   * that there is an @
   * that there are characters before and after the @
   * that there are no spaces
   * allows any number of characters before and after the @ 
  */
  const regEmail = /^[^\s@]+@[^\s@]+$/;
  const regPhone = /^\d{10}$/;

  /**
   * Create validation rule for each field.
   */
  const validator = createValidator<ApiUser>({    
    name: [
      (n) => !n || n.trim().length < 1 ? t('Le champ ne peut pas etre vide') : undefined,
    ],
    surname: [(n) => !n || n.trim().length < 1 ? t('Le champ ne peut pas etre vide' ): undefined,],
    company_id: [(n) => (!n ? t('Sélectionner une entreprise') : undefined)],
    email: [
      (n) => (!n ? t('Entrer un email') : undefined),
      (n) => (!regEmail.test(n) ? t('Email invalide' ): undefined),
    ],
    password: [(n) => !n || n.trim().length < 6 ? t('Entrer au moins 6 caractères') : undefined,],  
    tel: [
      (n) => {
        if (n === null || n === undefined || n === '') return undefined;
        return regPhone.test(n) ? undefined 
          : t('Le numéro doit contenir exactement 10 chiffres ou rester vide')
      }
    ],
    role_id: [(n) => (!n ? t('Sélectionner un Rôle') : undefined)]
  });

  // Define form hook.
  const methods = useForm(defaultValues, validator, onSubmit);       

  return StateDecorator([users, roles, companies], ref!, () => {
    assert(isDataState(users));
    assert(isDataState(roles));
    assert(isDataState(companies));

      // Transform users to a list of values.
    const roleList = Object.values(roles.data);
    const companyList = Object.values(companies.data);

    return (      
      <div className='customized-dialog-wrapper'>
        <CustomizedDialog
          title={title}
          open={isModalOpen}
          onCancel={
            () => {
              props.onCancel();
              methods.reset();
            }
          }
        >
          <form
            onSubmit={methods.onSubmit}
            className={`form ${props.className || ''}`}
          >
            <Stack spacing={2}>
              <TextInput
                label={t("Prénom")}
                name="name"
                placeholder="Entrer le prénom d'utilisateur"
                onChange={(value: string) => {
                  methods.setValue('name', value);
                }}
                required              
                errors={
                  methods.hasSubmitted
                    ? { tag: 'Submited', errors: methods.getErrors('name') }
                    : { tag: 'Not submitted' }
                }
              />

              <TextInput
                label={t("Nom")}
                name="surname"
                placeholder="Entrer le nom d'utilisateur"
                onChange={
                  (value) => {
                    methods.setValue('surname', value)
                  }
                }
                required              
                errors={
                  methods.hasSubmitted
                    ? { tag: 'Submited', errors: methods.getErrors('surname') }
                    : { tag: 'Not submitted' }
                }
              />

              <DropDownItemSelector
                onSelect={(selectedId) => {                
                  methods.setValue("company_id", selectedId)
                }}
                values={
                  companyList.map((company) => ({
                    id: company.id,
                    name: getOrElse(company.name),
                  }))
                }
                label={t("Entreprise d'appartenance")}
                disabled={false}
                name={"company_id"}    
                required            
                errors={
                  methods.hasSubmitted
                    ? {
                        tag: 'Submited',
                        errors: methods.getErrors('company_id'),
                      }
                    : { tag: 'Not submitted' }
              }/>

              <InputMultiType
                type='email'
                label={t("Email")}                
                name="email"
                autoComplete='on'  
                onChange={(value) => {
                  methods.setValue('email', value)
                }}
                required
                errors={
                  methods.hasSubmitted
                    ? { tag: 'Submited', errors: methods.getErrors('email') }
                    : { tag: 'Not submitted' }
                }
              />

              <InputMultiType
                label={t("Mot de passe")}               
                name="password"     
                type='password'                
                onChange={(value) => {
                  methods.setValue('password', value)
                }}
                required
                errors={
                  methods.hasSubmitted
                    ? { tag: 'Submited', errors: methods.getErrors('password') }
                    : { tag: 'Not submitted' }
                }
              />

              <InputMultiType
                label={t("Numéro de téléphone")}
                type="tel"
                name="name"                
                onChange={(value) => {
                  const v = value.toString()
                  methods.setValue('tel', v)
                }}              
                errors={
                  methods.hasSubmitted
                    ? { tag: 'Submited', errors: methods.getErrors('tel') }
                    : { tag: 'Not submitted' }
                }
              />

              <DropDownItemSelector
                onSelect={(selectedId) => {                
                  methods.setValue("role_id", selectedId)
                }}
                values={
                  roleList.map((role) => ({                    
                    id: role.id,
                    name: getOrElse(role.name)
                  }))
                }
                label={t("Rôle")}
                disabled={false}
                name={"role_id"}    
                required            
                errors={
                  methods.hasSubmitted
                    ? {
                        tag: 'Submited',
                        errors: methods.getErrors('role_id'),
                      }
                    : { tag: 'Not submitted' }
              }/>
              
              <Button 
                type="submit" 
                className="form-button-submit"
                variant="outlined"
                loadingPosition="end"
                data-testid="submit-project-button"
                endIcon={<SaveIcon />}
                >
                {state === 'Loading' ? t('Envoi...') : t('Créer')}
              </Button>
            </Stack>
          </form>
        </CustomizedDialog>
      </div>      
    );
  });
}

export default forwardRef(CreateUserForm);
